import {
	Button,
	Group,
	ScrollArea,
	Space,
	Table,
	type TableProps,
	Tooltip,
} from '@mantine/core';
import { IconPencilPlus, IconPencilStar } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundTableRow } from '../../../components/DataTable';
import { LoadingRows } from '../../../components/LoadingRows';
import { Paper } from '../../../components/Paper';
import { SearchField } from '../../../components/SearchField';
import { TablePageSelector } from '../../../components/TablePageSelector';
import { useModal } from '../../../hooks/useModal';
import { usePagination } from '../../../hooks/usePagination';
import { lightGreyIconButtonStyle } from '../../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { query } from '../../../util/api/query';
import { useBranchStockParams } from '../hooks/useBranchStockParams';
import type { BranchStockDto } from '../types/BranchStockDto';
import { BranchStockTableRow } from './BranchStockTableRow';
import { EditAllBranchStockModalPrices } from './EditAllBranchStockModalPrices';
import { NewBranchStockModal } from './NewBranchStockModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface BranchStockTableProps extends TableProps {
	skuid: string;
	vatRateId?: string;
}

export function BranchStockTableBox({
	skuid,
	vatRateId,
	...tableProps
}: BranchStockTableProps) {
	const { organisation } = useParams();
	const pagination = usePagination(20);

	const newBranchStockModal = useModal();
	const editAllPricesModal = useModal();

	const [branchSearch, setBranchSearch] = useState('');

	const { recordsParams, countParams } = useBranchStockParams(
		pagination,
		branchSearch,
	);

	const branchStockQuery = query<BranchStockDto[]>(
		`${organisation}/branch-stock/all/${skuid}?${recordsParams}`,
	);

	const branchStockCountQuery = query<number>(
		`${organisation}/branch-stock/count/${skuid}?${countParams}`,
	);

	const unavailableBranchQuery = query<string[]>(
		`${organisation}/branch-stock/unavailable-branch-ids/${encodeURIComponent(skuid)}`,
	);

	const userAccessQuery = query<boolean>(
		`${organisation}/branches/availability`,
	);

	const reloadTable = () => {
		branchStockQuery.remove();
		branchStockQuery.refetch();

		branchStockCountQuery.remove();
		branchStockCountQuery.refetch();
	};

	return (
		<>
			<EditAllBranchStockModalPrices
				close={editAllPricesModal.close}
				isOpened={editAllPricesModal.isOpen}
				updateBranchStockTable={() => reloadTable()}
				skuid={skuid}
				vatRateId={vatRateId}
			/>
			<NewBranchStockModal
				close={newBranchStockModal.close}
				isOpened={newBranchStockModal.isOpen}
				updateProductRecord={() => reloadTable()}
				unavailableBranchQuery={unavailableBranchQuery}
				skuid={skuid}
			/>
			<Paper
				title={'branch stocks'}
				headerContent={
					<Group mb={'xs'} justify="flex-end">
						<SearchField
							placeholder={'Search branches...'}
							onDelayComplete={(value) => setBranchSearch(value)}
							variant={'filled'}
						/>
						<Button
							{...lightGreyIconButtonStyle}
							onClick={() => newBranchStockModal.open()}
						>
							<IconPencilPlus {...defaultIconStyle} />
						</Button>
						<Tooltip
							label={
								userAccessQuery.data?.value
									? 'Edit all branch stock prices'
									: 'You must either be admin or have access to all branches to edit all branch stock prices'
							}
						>
							<Button
								{...lightGreyIconButtonStyle}
								onClick={() => editAllPricesModal.open()}
								disabled={!userAccessQuery.data?.value}
								loading={userAccessQuery.isLoading}
							>
								<IconPencilStar {...defaultIconStyle} />
							</Button>
						</Tooltip>
					</Group>
				}
			>
				<ScrollArea h={tableProps.h}>
					<Table stickyHeader h={'100%'} w={'100%'}>
						<Table.Thead>
							<KeyedRow>
								<Table.Th>Branch</Table.Th>
								<Table.Th>Area</Table.Th>
								<Table.Th>Description</Table.Th>
								<Table.Th>Stock Quantity</Table.Th>
								<Table.Th>Min Stock</Table.Th>
								<Table.Th>Max Stock</Table.Th>
								<Table.Th />
								<Table.Th />
							</KeyedRow>
						</Table.Thead>
						<Table.Tbody>
							{branchStockQuery.isLoading ? (
								<LoadingRows rows={20} />
							) : (
								<>
									{branchStockQuery.data?.value.map((branchStock) => (
										<BranchStockTableRow
											defaultBranchStock={branchStock}
											vatRateId={vatRateId}
											key={branchStock.branchId}
											refreshTable={() => {
												branchStockQuery.remove();
												branchStockCountQuery.remove();
												unavailableBranchQuery.remove();

												branchStockQuery.refetch();
												branchStockCountQuery.refetch();
												unavailableBranchQuery.refetch();
											}}
											skuid={encodeURIComponent(skuid)}
										/>
									))}
								</>
							)}
							{branchStockCountQuery.data &&
								branchStockCountQuery.data?.value < 1 && (
									<NoDataFoundTableRow pt={10} />
								)}
						</Table.Tbody>
					</Table>
				</ScrollArea>
				<Space h={18} />
				<TablePageSelector
					pageNumber={pagination.value.pageNumber}
					setPageNumber={pagination.setPageNumber}
					isLoading={branchStockCountQuery.isLoading}
					totalPages={pagination.getPageCount(
						branchStockCountQuery.data?.value,
					)}
				/>
			</Paper>
		</>
	);
}
